import { FC } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { CallIntelligenceOrgWetags } from '@wetags/call-intelligence';
import { getTaskArchivalPeriods } from '@frontend/call-intel-core';
import { InlineNotification, Text } from '@frontend/design-system';
import { useActiveFilters } from './hooks/use-active-filters';
import { useCallIntelDemoFlags } from './hooks/use-call-intel-demo-flags';
import { TaskArchivalMessage } from './task-archival-message';

type TaskArchivalBannerProps = {
  customStyles?: Interpolation<Theme>;
  taskCount?: number;
  variant?: 'summary' | 'drillDown';
};

export const TaskArchivalBanner: FC<TaskArchivalBannerProps> = ({
  customStyles,
  taskCount = 0,
  variant = 'summary',
}) => {
  const { filters } = useActiveFilters();

  const { isMixedActiveArchived, isAllArchived, isAllDeleted, isMixedArchivedDeleted, isMixedActiveArchivedDeleted } =
    getTaskArchivalPeriods({
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
  const { isDemoAccount } = useCallIntelDemoFlags();

  const [isCallIntelTaskArchivalEnabled] = CallIntelligenceOrgWetags.useWetag('call-intel-tasks-archival');

  if (!isCallIntelTaskArchivalEnabled || isDemoAccount) {
    return null;
  }

  if (variant === 'drillDown') {
    if (isMixedActiveArchived) {
      return (
        <InlineNotification status='info' size='medium' customStyles={[styles.wrapper, customStyles]}>
          <Text size='medium' css={styles.message}>
            <TaskArchivalMessage variant='archived' linkTaskCenter />
          </Text>
        </InlineNotification>
      );
    }

    if (isMixedActiveArchivedDeleted) {
      return (
        <InlineNotification status='info' size='medium' customStyles={[styles.wrapper, customStyles]}>
          <Text size='medium' css={styles.message}>
            <TaskArchivalMessage variant='archived-and-deleted' linkTaskCenter />
          </Text>
        </InlineNotification>
      );
    }

    return null;
  }

  if (!isAllDeleted && taskCount <= 0) {
    return null;
  }

  if (isMixedActiveArchived) {
    return (
      <InlineNotification status='info' size='medium' customStyles={[styles.wrapper, customStyles]}>
        <Text size='medium' css={styles.message}>
          <TaskArchivalMessage variant='archived-with-metrics' />
        </Text>
      </InlineNotification>
    );
  }

  if (isAllArchived) {
    return (
      <InlineNotification status='info' size='medium' customStyles={[styles.wrapper, customStyles]}>
        <Text size='medium' css={styles.message}>
          <TaskArchivalMessage variant='archived-with-metrics' linkTaskCenter />
        </Text>
      </InlineNotification>
    );
  }

  if (isMixedArchivedDeleted || isMixedActiveArchivedDeleted) {
    return (
      <InlineNotification status='info' size='medium' customStyles={[styles.wrapper, customStyles]}>
        <Text size='medium' css={styles.message}>
          <TaskArchivalMessage variant='archived-and-deleted' linkTaskCenter />
        </Text>
      </InlineNotification>
    );
  }

  if (isAllDeleted) {
    return (
      <InlineNotification status='info' size='medium' customStyles={[styles.wrapper, customStyles]}>
        <Text size='medium' css={styles.message}>
          <TaskArchivalMessage variant='deleted-adjust-filter' />
        </Text>
      </InlineNotification>
    );
  }

  return null;
};

const styles = {
  wrapper: css`
    max-width: 100%;
    max-height: none;

    p {
      white-space: normal;
    }
  `,

  message: css`
    margin: 0;
  `,
};
